import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { ColorPicker } from '@atlassian/color-picker/ui';
import { isReadable } from '@atlassian/color-picker/utils/color-conversion';
import { useBuiltInColorMap } from '@atlassian/color-picker/utils/use-built-in-color-map';
import { useProductDependencies } from '@atlassian/company-hub-common/controllers/use-product-dependencies';
import { RoundCornerIcon } from '@atlassian/company-hub-common/icons/round-corner-icon';
import { SharpCornerIcon } from '@atlassian/company-hub-common/icons/sharp-corner-icon';
import type { StyleOverrides } from '@atlassian/dynamic-cards-search-page/controllers/store/bootstrap';

import { messages } from './messages';

const cssStyles = cssMap({
	container: {
		paddingInline: token('space.100'),
		paddingBlockEnd: token('space.100'),
	},
	borderOptionContainer: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.050'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '24px',
		width: '24px',
	},
	border: {
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
	},
	selected: {
		backgroundColor: token('color.background.input'),
		borderColor: token('color.border.accent.blue'),
	},
	informationIcon: {
		paddingInlineStart: token('space.050'),
		paddingBlockStart: token('space.025'),
	},
});

export type StylesContentProps = {
	isSSRProductInDarkMode?: boolean;
	setStyles: (newStyles: StyleOverrides['textfield']) => void;
	styles: StyleOverrides['textfield'];
};

export const StylesContent = ({
	styles,
	setStyles,
	isSSRProductInDarkMode,
}: StylesContentProps): React.JSX.Element => {
	const { branding } = useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};
	const { backgroundColorMap, textColorMap, emptyColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);
	const { formatMessage } = useIntl();

	const fillColor = styles?.backgroundColor || '';
	const textColor = styles?.textColor || '';
	const cornerStyle = styles?.borderRadius || '';

	const isReadableFillColor = (color: string) => isReadable(color, textColor);
	const isReadableTextColor = (color: string) => isReadable(color, fillColor);

	return (
		<Stack space="space.300" xcss={cssStyles.container}>
			<Heading size="small">
				<FormattedMessage {...messages.stylesTitle} />
			</Heading>
			<Stack space="space.200">
				<Inline alignBlock="center" spread="space-between" testId="fill-color-wrapper">
					<Text color="color.text">
						<FormattedMessage {...messages.fillColorLabel} />
					</Text>
					<ColorPicker
						builtInColorMap={
							hideDefaultColorPalette && brandBackgroundColorMap
								? brandBackgroundColorMap
								: backgroundColorMap
						}
						onChange={(newFillColor: string) =>
							setStyles({ ...styles, backgroundColor: newFillColor || fillColor })
						}
						value={fillColor}
						pickerTitle={messages.fillColorLabel}
						showCustomPalette
						isReadableColor={isReadableFillColor}
					/>
				</Inline>
				<Inline alignBlock="center" spread="space-between" testId="text-color-wrapper">
					<Text color="color.text">
						<FormattedMessage {...messages.textColorLabel} />
					</Text>
					<ColorPicker
						builtInColorMap={hideDefaultColorPalette ? emptyColorMap : textColorMap}
						onChange={(newTextColor: string) =>
							setStyles({ ...styles, textColor: newTextColor || textColor })
						}
						value={textColor}
						pickerTitle={messages.textColorLabel}
						showCustomPalette
						showTextIcon
						isReadableColor={isReadableTextColor}
					/>
				</Inline>
				<Inline alignBlock="center" spread="space-between" testId="corner-style-wrapper">
					<Text color="color.text">
						<FormattedMessage {...messages.cornerStyleLabel} />
					</Text>
					<Inline space="space.075">
						{[
							{
								cornerOption: token('radius.large'),
								IconComponent: RoundCornerIcon,
								label: messages.roundCornerStyleLabel,
								textIdPrefix: 'round',
							},
							{
								cornerOption: '0px',
								IconComponent: SharpCornerIcon,
								label: messages.sharpCornerStyleLabel,
								textIdPrefix: 'sharp',
							},
						].map(({ cornerOption, IconComponent, label, textIdPrefix }) => (
							<Tooltip
								content={<FormattedMessage {...label} />}
								hideTooltipOnMouseDown
								key={cornerOption}
							>
								<Pressable
									xcss={cx(
										cssStyles.borderOptionContainer,
										cssStyles.border,
										cornerStyle === cornerOption && cssStyles.selected,
									)}
									onClick={() => setStyles({ ...styles, borderRadius: cornerOption })}
									testId={`${textIdPrefix}${cornerStyle === cornerOption ? '--selected' : ''}`}
								>
									<IconComponent
										color={
											cornerStyle === cornerOption
												? token('color.icon.accent.blue')
												: token('color.icon')
										}
										label={formatMessage(label)}
									/>
								</Pressable>
							</Tooltip>
						))}
					</Inline>
				</Inline>
				<Inline alignBlock="center" spread="space-between" testId="placeholder-text-wrapper">
					<Stack space="space.100" grow="fill">
						<Inline alignBlock="center">
							<Text color="color.text">
								<FormattedMessage {...messages.placeholderTextLabel} />
							</Text>
							<Tooltip content={formatMessage(messages.placeholderTooltipLabel)}>
								<Box xcss={cssStyles.informationIcon}>
									<StatusInformationIcon color={token('color.icon.subtlest')} label="" />
								</Box>
							</Tooltip>
						</Inline>
						<Textfield
							aria-label={formatMessage(messages.placeholderTextLabel)}
							onChange={(e) => setStyles({ ...styles, placeholder: e.currentTarget.value || '' })}
							value={styles?.placeholder || formatMessage(messages.defaultPlaceholderText)}
						/>
					</Stack>
				</Inline>
			</Stack>
		</Stack>
	);
};
